import { useCallback, useEffect, useState } from 'react'
import { adDetectionApi, authApi } from '../../api'
import { useAuth } from '../../AuthContext'
import { ErrorState, errorMessage } from '../../components/ErrorState'
import type { AdDetectionStats, DetectedAdvertiserRow, FiscalStatus, PaginatedResponse, VerificationStatus } from '../../types'
import { useShell } from '../../shell/ShellContext'
import { currentQuery, navigate, useRoute, withQuery } from '../../shell/router'
import { Block, PageHeader, SidePanel, StatusPill, ViewTabs } from '../../ui/ui'
import { useI18n } from '../../i18n'
import { useAds } from './AdsContext'
import {
  categoryLabel,
  detectionTypeLabel,
  FISCAL_STATUSES,
  fiscalLabel,
  FiscalPill,
  PLATFORMS,
  platformLabel,
  SENSITIVE_CATEGORIES,
  VERIFICATION_STATUSES,
  verifLabel,
  VERIF_TRANSITIONS,
  VerificationPill,
  hostname,
  screenshotUrl,
} from './adsShared'
import { countryName } from './AdsModule'

type SavedView = 'review' | 'in_review' | 'unregistered' | 'sensitive' | 'all'

/** Vues enregistrées : on part de la question qu'on se pose, pas des critères. */
const VIEW_FILTERS: Record<SavedView, Record<string, string>> = {
  review: { verificationStatus: 'NEW,TO_REVIEW' },
  in_review: { verificationStatus: 'IN_REVIEW,ESCALATED' },
  unregistered: { fiscalStatus: 'NOT_REGISTERED' },
  sensitive: { detectedCategory: SENSITIVE_CATEGORIES[0] },
  all: {},
}

const FILTER_KEYS = ['search', 'platform', 'type', 'category', 'fiscal', 'verification', 'sort'] as const

export function AdsAdvertisers() {
  useRoute()
  const { t, fmt } = useI18n()
  const { country } = useShell()
  const { refreshKey } = useAds()
  const query = currentQuery()
  const view = (query.get('view') as SavedView) || 'review'
  const selectedId = query.get('id')
  const page = Number(query.get('page')) || 1
  const params: Record<string, string> = {}
  for (const k of FILTER_KEYS) {
    const v = query.get(k)
    if (v) params[k] = v
  }

  const [data, setData] = useState<PaginatedResponse<DetectedAdvertiserRow> | null>(null)
  const [stats, setStats] = useState<AdDetectionStats | null>(null)
  const [categories, setCategories] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState(params.search ?? '')

  const [sortBy, sortOrder] = (params.sort ?? 'lastSeenAt:desc').split(':')
  const request = {
    ...VIEW_FILTERS[view],
    country: country || undefined,
    search: params.search,
    platform: params.platform,
    detectionType: params.type,
    detectedCategory: params.category ?? VIEW_FILTERS[view].detectedCategory,
    fiscalStatus: params.fiscal ?? VIEW_FILTERS[view].fiscalStatus,
    verificationStatus: params.verification ?? VIEW_FILTERS[view].verificationStatus,
    sortBy,
    sortOrder,
    page,
    limit: 20,
  }
  const requestKey = JSON.stringify(request)

  const go = (patch: Record<string, string | undefined>) => {
    navigate(withQuery('ads/advertisers', { view, ...params, id: selectedId ?? undefined, page: undefined, ...patch }))
  }

  const load = useCallback(() => {
    setError(null)
    adDetectionApi
      .getAdvertisers(JSON.parse(requestKey))
      .then(setData)
      .catch((err) => setError(errorMessage(err)))
  }, [requestKey])

  useEffect(load, [load, refreshKey])

  useEffect(() => {
    adDetectionApi.getStats(country || undefined).then(setStats).catch(() => setStats(null))
    adDetectionApi.getCategories().then(setCategories).catch(() => setCategories([]))
  }, [country, refreshKey])

  const counts: Partial<Record<SavedView, number>> = stats
    ? {
        review: (stats.byVerification.NEW ?? 0) + (stats.byVerification.TO_REVIEW ?? 0),
        in_review: (stats.byVerification.IN_REVIEW ?? 0) + (stats.byVerification.ESCALATED ?? 0),
        unregistered: stats.byFiscalStatus.NOT_REGISTERED ?? 0,
        sensitive: SENSITIVE_CATEGORIES.reduce((n, c) => n + (stats.byCategory[c] ?? 0), 0),
        all: stats.totalAdvertisers,
      }
    : {}

  const selected = data?.data.find((r) => r.id === selectedId) ?? null
  const activeFilters = FILTER_KEYS.filter((k) => k !== 'sort' && k !== 'search' && params[k]).length

  return (
    <>
      <PageHeader
        title={t('ads.advertisersTitle')}
        description={
          stats
            ? t('ads.advertisersCount', {
                n: fmt.number(stats.totalAdvertisers),
                country: country ? ` · ${countryName(t, country)}` : '',
                review: counts.review ?? 0,
              })
            : t('ads.advertisersDesc')
        }
        actions={
          country ? (
            <a className="btn btn-secondary" href={adDetectionApi.getExportUrl(country)} download>
              {t('ads.exportCsv')}
            </a>
          ) : (
            <span className="block-sub">{t('ads.pickCountryToExport')}</span>
          )
        }
      />

      <ViewTabs
        label={t('ads.savedViews')}
        value={view}
        onChange={(v) => navigate(withQuery('ads/advertisers', { view: v }))}
        options={[
          { value: 'review', label: t('ads.view.review'), count: counts.review },
          { value: 'in_review', label: t('ads.view.in_review'), count: counts.in_review },
          { value: 'unregistered', label: t('ads.view.unregistered'), count: counts.unregistered },
          { value: 'sensitive', label: t('ads.view.sensitive'), count: counts.sensitive },
          { value: 'all', label: t('ads.view.all'), count: counts.all },
        ]}
      />

      <div className="ads-filters" role="group" aria-label={t('ads.filters')}>
        <form
          className="ads-search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault()
            go({ search: search.trim() || undefined })
          }}
        >
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('ads.searchPlaceholder')} aria-label={t('ads.searchAria')} />
        </form>
        <select aria-label={t('ads.channel')} value={params.platform ?? ''} onChange={(e) => go({ platform: e.target.value || undefined })}>
          <option value="">{t('ads.allChannels')}</option>
          {PLATFORMS.map((k) => <option key={k} value={k}>{platformLabel(t, k)}</option>)}
        </select>
        <select aria-label={t('common.type')} value={params.type ?? ''} onChange={(e) => go({ type: e.target.value || undefined })}>
          <option value="">{t('ads.paidAndOrganic')}</option>
          <option value="SPONSORED">{t('ads.paidAds')}</option>
          <option value="ORGANIC">{t('ads.organicResults')}</option>
        </select>
        <select aria-label={t('ads.sector')} value={params.category ?? ''} onChange={(e) => go({ category: e.target.value || undefined })}>
          <option value="">{t('ads.allSectors')}</option>
          {categories.map((c) => <option key={c} value={c}>{categoryLabel(t, c)}</option>)}
        </select>
        <select aria-label={t('ads.fiscalStatus')} value={params.fiscal ?? ''} onChange={(e) => go({ fiscal: e.target.value || undefined })}>
          <option value="">{t('ads.allFiscalStatuses')}</option>
          {FISCAL_STATUSES.map((s) => <option key={s} value={s}>{fiscalLabel(t, s)}</option>)}
        </select>
        <select aria-label={t('ads.verification')} value={params.verification ?? ''} onChange={(e) => go({ verification: e.target.value || undefined })}>
          <option value="">{view === 'all' ? t('ads.allVerifications') : t('ads.viewVerification')}</option>
          {VERIFICATION_STATUSES.map((s) => <option key={s} value={s}>{verifLabel(t, s)}</option>)}
        </select>
        <select aria-label={t('ads.sort')} value={params.sort ?? 'lastSeenAt:desc'} onChange={(e) => go({ sort: e.target.value === 'lastSeenAt:desc' ? undefined : e.target.value })}>
          <option value="lastSeenAt:desc">{t('ads.sortRecent')}</option>
          <option value="adCount:desc">{t('ads.sortAdCount')}</option>
          <option value="estimatedSpendUpper:desc">{t('ads.sortSpend')}</option>
          <option value="advertiserName:asc">{t('ads.sortName')}</option>
        </select>
        {activeFilters > 0 && (
          <button type="button" className="block-link" onClick={() => navigate(withQuery('ads/advertisers', { view }))}>
            {t('ads.clearFilters')}
          </button>
        )}
      </div>

      <Block flush>
        {error ? (
          <ErrorState message={error} onRetry={load} />
        ) : !data ? (
          <div className="stat-skeleton" style={{ minHeight: 240, margin: 16 }} />
        ) : data.data.length === 0 ? (
          <div className="table-empty">{t('ads.noAdvertiserMatch')}</div>
        ) : (
          <div className="table-scroll">
            <table className="bet-table bet-table-clickable">
              <thead>
                <tr>
                  <th scope="col">{t('ads.colAdvertiser')}</th>
                  <th scope="col">{t('ads.channel')}</th>
                  <th scope="col">{t('ads.sector')}</th>
                  <th scope="col">{t('ads.fiscalStatus')}</th>
                  <th scope="col">{t('ads.verification')}</th>
                  <th scope="col" className="num-col">{t('ads.colAdCount')}</th>
                  <th scope="col">{t('ads.colSeenOn')}</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((r) => (
                  <tr
                    key={r.id}
                    tabIndex={0}
                    className={r.id === selectedId ? 'bet-row-selected' : undefined}
                    onClick={() => go({ id: r.id })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') go({ id: r.id })
                    }}
                  >
                    <td>
                      <div className="bet-service-cell">
                        <strong>{r.advertiserName}</strong>
                        <span>{r.domain || hostname(r.sourceUrl) || (country ? '' : countryName(t, r.country))}</span>
                      </div>
                    </td>
                    <td>
                      <div className="bet-service-cell">
                        <span className="bet-cell-strong">{platformLabel(t, r.platform)}</span>
                        {r.detectionType === 'ORGANIC' && <span>{t('ads.type.ORGANIC')}</span>}
                      </div>
                    </td>
                    <td>{r.detectedCategory ? categoryLabel(t, r.detectedCategory) : <span className="bet-muted">—</span>}</td>
                    <td><FiscalPill status={r.fiscalStatus} /></td>
                    <td><VerificationPill status={r.verificationStatus} /></td>
                    <td className="num num-col">{r.adCount}</td>
                    <td className="num">{fmt.date(r.lastSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && data.totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => go({ page: String(page - 1), id: undefined })}>← {t('common.previous')}</button>
            <span className="page-info">
              {t('ads.rangeInfo', {
                from: (page - 1) * data.limit + 1,
                to: Math.min(page * data.limit, data.total),
                total: data.total,
              })}
            </span>
            <button disabled={page >= data.totalPages} onClick={() => go({ page: String(page + 1), id: undefined })}>{t('common.next')} →</button>
          </div>
        )}
      </Block>

      {selected && (
        <AdvertiserPanel
          advertiser={selected}
          onClose={() => go({ id: undefined, page: page > 1 ? String(page) : undefined })}
          onChanged={(updated) => setData((d) => (d ? { ...d, data: d.data.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)) } : d))}
        />
      )}
    </>
  )
}

function AdvertiserPanel({
  advertiser: a,
  onClose,
  onChanged,
}: {
  advertiser: DetectedAdvertiserRow
  onClose: () => void
  onChanged: (row: DetectedAdvertiserRow) => void
}) {
  const { user } = useAuth()
  const { t, fmt } = useI18n()
  const transitions = VERIF_TRANSITIONS[a.verificationStatus ?? 'NEW'] ?? []
  const logs = a.verificationLogs ?? []
  const lastAssignee = [...logs].reverse().find((l) => l.assignedTo)?.assignedTo ?? ''
  const [agents, setAgents] = useState<Array<{ id: string; fullName: string; email: string }> | null>(null)
  const [target, setTarget] = useState<VerificationStatus | ''>(transitions[0] ?? '')
  const [agent, setAgent] = useState(lastAssignee || user?.fullName || '')
  const [comment, setComment] = useState('')
  const [fiscal, setFiscal] = useState<FiscalStatus>(a.fiscalStatus ?? 'UNKNOWN')
  const [fiscalNotes, setFiscalNotes] = useState(a.fiscalNotes ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAllAds, setShowAllAds] = useState(false)

  useEffect(() => {
    authApi.getAgents().then(setAgents).catch(() => setAgents([]))
  }, [])

  // Nouvel annonceur sélectionné : le formulaire repart de son état.
  useEffect(() => {
    const t = VERIF_TRANSITIONS[a.verificationStatus ?? 'NEW'] ?? []
    setTarget(t[0] ?? '')
    setComment('')
    setFiscal(a.fiscalStatus ?? 'UNKNOWN')
    setFiscalNotes(a.fiscalNotes ?? '')
    setAgent([...(a.verificationLogs ?? [])].reverse().find((l) => l.assignedTo)?.assignedTo ?? user?.fullName ?? '')
    setError(null)
    setShowAllAds(false)
  }, [a.id, a.verificationStatus, a.fiscalStatus, a.fiscalNotes, a.verificationLogs, user?.fullName])

  const saveVerification = async () => {
    if (!target) return
    setBusy(true)
    setError(null)
    try {
      onChanged(await adDetectionApi.updateVerificationStatus(a.id, target, comment.trim(), agent.trim()))
      setComment('')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const saveFiscal = async () => {
    setBusy(true)
    setError(null)
    try {
      onChanged(await adDetectionApi.updateFiscalStatus(a.id, fiscal, fiscalNotes.trim() || undefined))
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const site = a.domain || hostname(a.sourceUrl)
  const metaLibrary =
    a.platform === 'META' && a.pageId
      ? `https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=${a.country}&view_all_page_id=${a.pageId}`
      : null
  const ads = showAllAds ? a.sampleAds : a.sampleAds.slice(0, 3)
  const fiscalDirty = fiscal !== (a.fiscalStatus ?? 'UNKNOWN') || fiscalNotes !== (a.fiscalNotes ?? '')

  return (
    <SidePanel
      wide
      title={a.advertiserName}
      subtitle={
        <span className="ads-panel-sub">
          {site && (a.sourceUrl ? <a href={a.sourceUrl} target="_blank" rel="noopener noreferrer">{site}</a> : <span>{site}</span>)}
          <span>{countryName(t, a.country)}</span>
        </span>
      }
      onClose={onClose}
    >
      <div className="ads-panel-pills">
        {a.detectedCategory && (
          <StatusPill tone={SENSITIVE_CATEGORIES.includes(a.detectedCategory) ? 'warn' : 'info'}>
            {SENSITIVE_CATEGORIES.includes(a.detectedCategory) ? t('ads.sensitivePrefix') : ''}
            {categoryLabel(t, a.detectedCategory)}
          </StatusPill>
        )}
        <FiscalPill status={a.fiscalStatus} />
        <VerificationPill status={a.verificationStatus} />
      </div>

      {error && <ErrorState message={error} compact />}

      <section className="panel-section">
        <span className="panel-section-title">{t('ads.whereSeen')}</span>
        <dl className="field-grid">
          <div className="field"><dt>{t('ads.channel')}</dt><dd>{platformLabel(t, a.platform)}{a.detectionType ? ` · ${detectionTypeLabel(t, a.detectionType)}` : ''}</dd></div>
          <div className="field"><dt>{t('ads.query')}</dt><dd>{a.sourceQuery ? `« ${a.sourceQuery} »` : '—'}</dd></div>
          <div className="field"><dt>{t('ads.firstSeen')}</dt><dd className="num">{fmt.date(a.firstSeenAt)}</dd></div>
          <div className="field"><dt>{t('ads.lastSeen')}</dt><dd className="num">{fmt.date(a.lastSeenAt)}</dd></div>
          <div className="field"><dt>{t('ads.colAdCount')}</dt><dd className="num">{fmt.number(a.adCount)}</dd></div>
          <div className="field">
            <dt>{t('ads.estimatedSpend')}</dt>
            <dd className="num">{a.estimatedSpendUpper ? `${a.estimatedSpendLower ?? 0} – ${a.estimatedSpendUpper}` : '—'}</dd>
          </div>
        </dl>
        {(a.pageTitle || a.snippet) && (
          <div className="ads-snippet">
            {a.pageTitle && a.pageTitle !== a.advertiserName && <strong>{a.pageTitle}</strong>}
            {a.snippet && <span>{a.snippet}</span>}
          </div>
        )}
      </section>

      {(a.sampleAds.length > 0 || metaLibrary || a.screenshotPath) && (
        <section className="panel-section">
          <div className="bet-panel-head">
            <span className="panel-section-title">{t('ads.adsPreview')}</span>
            {metaLibrary && (
              <a className="block-link" href={metaLibrary} target="_blank" rel="noopener noreferrer">{t('ads.metaLibrary')}</a>
            )}
          </div>
          {a.screenshotPath && (
            <a className="ads-capture" href={screenshotUrl(a.screenshotPath)} target="_blank" rel="noopener noreferrer" title={t('ads.openCapture')}>
              <img src={screenshotUrl(a.screenshotPath)} alt={t('ads.captureAlt', { name: a.advertiserName })} loading="lazy" />
            </a>
          )}
          {a.sampleAds.length > 0 && (
            <ul className="ads-samples">
              {ads.map((ad, i) => (
                <li key={ad.adArchiveId || i}>
                  {ad.imageUrl && (
                    <img
                      src={ad.imageUrl}
                      alt={ad.title ?? t('ads.adAlt', { n: i + 1 })}
                      loading="lazy"
                      onError={(e) => {
                        ;(e.target as HTMLImageElement).hidden = true
                      }}
                    />
                  )}
                  <div>
                    {ad.title && <strong>{ad.title}</strong>}
                    {ad.body && <p>{ad.body}</p>}
                    <span className="ads-sample-links">
                      {ad.linkUrl && <a href={ad.linkUrl} target="_blank" rel="noopener noreferrer">{hostname(ad.linkUrl)}</a>}
                      <a href={ad.adLibraryUrl} target="_blank" rel="noopener noreferrer">{t('ads.viewAd')}</a>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {a.sampleAds.length > 3 && (
            <button type="button" className="block-link" onClick={() => setShowAllAds((v) => !v)}>
              {showAllAds ? t('ads.showLessAds') : t('ads.showAllAds', { n: a.sampleAds.length })}
            </button>
          )}
        </section>
      )}

      <section className="panel-section">
        <span className="panel-section-title">{t('ads.verification')}</span>
        {transitions.length > 0 ? (
          <div className="form-stack">
            <div className="bet-form-row">
              <label>
                {t('ads.newStatus')}
                <select value={target} onChange={(e) => setTarget(e.target.value as VerificationStatus)}>
                  {transitions.map((s) => (
                    <option key={s} value={s}>{s === 'ARCHIVED' ? t('ads.verif.ARCHIVED_long') : verifLabel(t, s)}</option>
                  ))}
                </select>
              </label>
              <label>
                {t('ads.agentInCharge')}
                <select value={agent} onChange={(e) => setAgent(e.target.value)} disabled={agents === null}>
                  <option value="">{agents === null ? t('common.loading') : t('ads.pickAgent')}</option>
                  {agent && agents && !agents.some((x) => x.fullName === agent) && <option value={agent}>{agent}</option>}
                  {(agents ?? []).map((x) => <option key={x.id} value={x.fullName}>{x.fullName}</option>)}
                </select>
              </label>
            </div>
            <label>
              {t('ads.commentRequired')}
              <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('ads.commentPlaceholder')} />
            </label>
            <div className="bet-form-actions">
              <button type="button" className="btn btn-primary" disabled={busy || !target || !comment.trim() || !agent.trim()} onClick={saveVerification}>
                {busy ? t('common.saving') : t('ads.saveVerification')}
              </button>
            </div>
          </div>
        ) : (
          <p className="empty-note">{t('ads.noNextStep')}</p>
        )}

        {logs.length > 0 && (
          <ol className="bet-history">
            {[...logs].reverse().map((l, i) => (
              <li key={`${l.at}-${i}`}>
                <span className="bet-history-dot" aria-hidden="true" />
                <div>
                  <span className="bet-history-what">{verifLabel(t, l.from)} → {verifLabel(t, l.to)}</span>
                  {l.comment && <span className="bet-history-comment">{l.comment}</span>}
                  <span className="bet-history-meta">
                    {l.agent}{l.assignedTo ? ` · ${t('ads.inChargeOf', { who: l.assignedTo })}` : ''} · {fmt.dateTime(l.at)}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="panel-section">
        <span className="panel-section-title">{t('ads.fiscalStatus')}</span>
        <div className="form-stack">
          <label>
            {t('common.status')}
            <select value={fiscal} onChange={(e) => setFiscal(e.target.value as FiscalStatus)}>
              {FISCAL_STATUSES.map((s) => <option key={s} value={s}>{fiscalLabel(t, s)}</option>)}
            </select>
          </label>
          <label>
            {t('common.notes')}
            <textarea rows={2} value={fiscalNotes} onChange={(e) => setFiscalNotes(e.target.value)} placeholder={t('ads.fiscalNotesPlaceholder')} />
          </label>
          {fiscalDirty && (
            <div className="bet-form-actions">
              <button type="button" className="btn btn-secondary" disabled={busy} onClick={saveFiscal}>{t('ads.saveFiscal')}</button>
            </div>
          )}
        </div>
      </section>
    </SidePanel>
  )
}
